import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CalendarPlus, Plus, Trash2 } from 'lucide-react';
import {
  getCatalogs, createClass, updateClass, getClass,
  ClassCatalogs, ScheduleBlockInput, DAYS,
} from '../api';
import { useToast } from '../../../context/ToastContext';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

// Orden de días para el selector: Lun..Dom
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const emptyBlock = (classroomId: number | null): ScheduleBlockInput => ({
  weekday: 1, start_time: '18:00', end_time: '19:00', classroom_id: classroomId,
});
/** "16:00" + 1 h = "17:00" (tope 23:59: una clase no cruza la medianoche). */
function masUnaHora(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '19:00';
  const t = Math.min(23 * 60 + 59, h * 60 + m + 60);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** Valores con los que abrir el alta (clic en un hueco del calendario). */
export interface ClassFormInitial {
  weekday?: number;
  start_time?: string;   // 'HH:MM'
  end_time?: string;     // 'HH:MM' — si falta, 1 hora después del inicio
  classroom_id?: number | null;
  date?: string;         // 'YYYY-MM-DD' de la columna (calendario semanal): la usa una renta/ensayo de un día
}

/** Día de la semana (0 = domingo) de 'YYYY-MM-DD' en hora LOCAL (new Date('YYYY-MM-DD') es UTC y en Tijuana retrocede un día). */
function weekdayDe(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

/**
 * Modal de alta/edición de una clase. Si recibe `editId`, carga y precarga sus datos.
 * `initial` solo aplica al ALTA (clic en un hueco del calendario: día + hora ya puestos).
 * onSaved(id) recibe el class_id creado/editado.
 */
export default function ClassForm({ editId, initial, onClose, onSaved }:
  { editId?: number; initial?: ClassFormInitial; onClose: () => void; onSaved: (id: number) => void }) {
  const toast = useToast();
  const editing = editId != null;
  const [cat, setCat] = useState<ClassCatalogs | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [styleId, setStyleId] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [followerId, setFollowerId] = useState('');
  const [classroomId, setClassroomId] = useState('');
  const [capacity, setCapacity] = useState('');
  // Precio de clase suelta PROPIO de esta clase (Karla 30-sep). Vacío = regla general ($120 L-V / $220 sábado).
  const [precio, setPrecio] = useState('');
  const [levelCode, setLevelCode] = useState('');
  const [color, setColor] = useState('#2D698A');
  const [description, setDescription] = useState('');
  // VIGENCIA del ciclo (29-jul-2026): el horario es semanal, pero la clase solo corre
  // entre estas dos fechas. Sin ellas se repetiría para siempre en el calendario.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [blocks, setBlocks] = useState<ScheduleBlockInput[]>([]);
  // RENTA / ENSAYO DE UN DÍA (Karla 8-oct-2026): se capturaban en este mismo formulario de clase semanal sin
  // fechas y se repetían TODAS las semanas (6 activas así: «Frank», «Ensayo NOLI»). Una renta se guarda como
  // registro de un solo día: start_date = end_date = fecha, un bloque en el día de la semana de esa fecha.
  const [tipo, setTipo] = useState<'clase' | 'renta'>('clase');
  const [fecha, setFecha] = useState('');
  const [avisoSinFin, setAvisoSinFin] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const c = await getCatalogs();
        setCat(c.data);
        const firstRoom = c.data.classrooms[0]?.classroom_id ?? null;
        if (editing) {
          const r = await getClass(editId!);
          const d = r.data;
          setName(d.name || '');
          setStyleId(d.style_id ? String(d.style_id) : '');
          setLeaderId(d.teacher_leader_id ? String(d.teacher_leader_id) : '');
          setFollowerId(d.teacher_follower_id ? String(d.teacher_follower_id) : '');
          setClassroomId(d.classroom_id ? String(d.classroom_id) : '');
          setCapacity(d.capacity != null ? String(d.capacity) : '');
          setPrecio(d.price != null ? String(Number(d.price)) : '');
          setLevelCode(d.level_code || '');
          setColor(/^#[0-9a-fA-F]{6}$/.test(d.color) ? d.color : '#2D698A');
          setDescription(d.description || '');
          setStartDate((d.start_date || '').slice(0, 10));
          setEndDate((d.end_date || '').slice(0, 10));
          const ini = (d.start_date || '').slice(0, 10);
          if (ini && ini === (d.end_date || '').slice(0, 10)) { setTipo('renta'); setFecha(ini); }
          setBlocks((d.schedule || []).map((s: any) => ({
            weekday: s.weekday,
            start_time: (s.start_time || '').slice(0, 5),
            end_time: (s.end_time || '').slice(0, 5),
            classroom_id: s.classroom_id ?? firstRoom,
          })));
        } else if (initial) {
          // Alta desde un hueco del calendario: día, hora y salón ya puestos (duración 1 h).
          const base = emptyBlock(firstRoom);
          const start = initial.start_time || base.start_time;
          const room = initial.classroom_id !== undefined ? initial.classroom_id : firstRoom;
          setBlocks([{
            weekday: initial.weekday ?? base.weekday,
            start_time: start,
            end_time: initial.end_time || masUnaHora(start),
            classroom_id: room,
          }]);
          if (initial.classroom_id != null) setClassroomId(String(initial.classroom_id));
          if (initial.date) setFecha(initial.date);
        } else {
          setBlocks([emptyBlock(firstRoom)]);
        }
      } catch (e: any) {
        toast.error(e?.message || 'No se pudieron cargar los catálogos');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ===== Horario SIMPLE (Karla 30-jul: «me pone el horario por día… es muy tedioso»).
  // Como Kydemy: una sola hora + los días marcados. Solo se cae al modo por bloques cuando
  // la clase de verdad tiene horas distintas según el día (las hay en su ciclo importado).
  const mismoHorario = blocks.length > 0 && blocks.every(
    (b) => b.start_time === blocks[0].start_time && b.end_time === blocks[0].end_time && b.classroom_id === blocks[0].classroom_id);
  const [avanzado, setAvanzado] = useState(false);
  // Al cargar una clase existente, el modo lo decide su horario real (una sola vez).
  const modoDecidido = useRef(false);
  useEffect(() => {
    if (loading || modoDecidido.current) return;
    modoDecidido.current = true;
    if (blocks.length > 1 && !mismoHorario) setAvanzado(true);
  }, [loading, blocks, mismoHorario]);

  const dias = mismoHorario ? blocks.map((b) => b.weekday) : [];
  // La hora/salón sobreviven a quedarse sin días marcados (si no, al desmarcar todo y volver
  // a marcar, la hora que ella acababa de teclear se perdía).
  const ultimaBase = useRef<ScheduleBlockInput | null>(null);
  if (blocks[0]) ultimaBase.current = blocks[0];
  function toggleDia(d: number) {
    const base = blocks[0] || ultimaBase.current || emptyBlock(cat?.classrooms[0]?.classroom_id ?? null);
    const yaEsta = dias.includes(d);
    const nuevos = yaEsta ? dias.filter((x) => x !== d) : [...dias, d];
    setBlocks(DAY_ORDER.filter((x) => nuevos.includes(x)).map((weekday) => ({ ...base, weekday })));
  }
  // En modo simple la hora y el salón son de TODA la clase: se aplican a cada día marcado.
  function setHoraTodos(patch: Partial<ScheduleBlockInput>) {
    setBlocks((b) => (b.length ? b.map((bl) => ({ ...bl, ...patch })) : [{ ...emptyBlock(cat?.classrooms[0]?.classroom_id ?? null), ...patch }]));
  }

  function updateBlock(i: number, patch: Partial<ScheduleBlockInput>) {
    setBlocks((b) => b.map((bl, j) => (j === i ? { ...bl, ...patch } : bl)));
  }
  function addBlock() {
    const firstRoom = cat?.classrooms[0]?.classroom_id ?? null;
    setBlocks((b) => [...b, emptyBlock(firstRoom)]);
  }
  function removeBlock(i: number) { setBlocks((b) => b.filter((_, j) => j !== i)); }

  function cambiarTipo(t: 'clase' | 'renta') {
    setTipo(t);
    setAvisoSinFin(false);
    if (t === 'renta' && !fecha) setFecha(startDate || initial?.date || '');
  }

  async function submit(confirmadoSinFin = false) {
    if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (tipo === 'renta') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) { toast.error('Elige la fecha de la renta o ensayo'); return; }
      const base = blocks[0] || ultimaBase.current || emptyBlock(cat?.classrooms[0]?.classroom_id ?? null);
      if (base.end_time <= base.start_time) { toast.error('Revisa el horario: la hora de fin debe ser mayor a la de inicio'); return; }
      setSaving(true);
      try {
        const body = {
          name: name.trim(),
          description: description.trim() || null,
          style_id: null, level_code: null, capacity: null, price: null,
          teacher_leader_id: leaderId ? Number(leaderId) : null,
          teacher_follower_id: null,
          classroom_id: classroomId ? Number(classroomId) : null,
          color,
          start_date: fecha,
          end_date: fecha,
          schedule: [{ ...base, weekday: weekdayDe(fecha) }],
        };
        const r = editing ? await updateClass(editId!, body) : await createClass(body);
        toast.success(editing ? 'Renta actualizada' : 'Renta registrada para ese día');
        onSaved(r.data.class_id);
      } catch (e: any) {
        toast.error(e?.message || 'No se pudo guardar');
      } finally { setSaving(false); }
      return;
    }
    // Alta de clase semanal SIN fecha de fin = se repite para siempre. Casi siempre era una renta (8-oct).
    if (!editing && !endDate && !confirmadoSinFin) { setAvisoSinFin(true); return; }
    for (const bl of blocks) {
      if (bl.end_time <= bl.start_time) { toast.error('Revisa el horario: la hora de fin debe ser mayor a la de inicio'); return; }
    }
    if (startDate && endDate && endDate < startDate) { toast.error('La fecha de fin no puede ser anterior a la de inicio'); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        style_id: styleId ? Number(styleId) : null,
        level_code: levelCode.trim() || null,
        capacity: capacity ? Number(capacity) : null,
        price: precio.trim() ? Number(precio) : null,
        teacher_leader_id: leaderId ? Number(leaderId) : null,
        teacher_follower_id: followerId ? Number(followerId) : null,
        classroom_id: classroomId ? Number(classroomId) : null,
        color,
        start_date: startDate || null,
        end_date: endDate || null,
        schedule: blocks,
      };
      const r = editing ? await updateClass(editId!, body) : await createClass(body);
      toast.success(editing ? 'Clase actualizada' : 'Clase creada');
      onSaved(r.data.class_id);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="modal-panel max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-5 py-3.5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            <CalendarPlus size={18} className="text-brand-blue" /> {tipo === 'renta' ? (editing ? 'Editar renta o ensayo' : 'Nueva renta o ensayo') : (editing ? 'Editar clase' : 'Nueva clase')}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-10 w-full animate-pulse rounded-lg bg-skeleton" />)}</div>
        ) : (
          <div className="space-y-4 p-5">
            <div>
              <label className={labelCls}>¿Qué vas a registrar?</label>
              <div className="grid grid-cols-2 gap-2" data-testid="tipo-registro">
                {([['clase', 'Clase semanal', 'Se repite cada semana'], ['renta', 'Renta o ensayo', 'Un solo día']] as const).map(([v, t, sub]) => (
                  <button key={v} type="button" onClick={() => cambiarTipo(v)}
                    className={`rounded-lg border px-3 py-2 text-left transition ${tipo === v
                      ? 'border-accent bg-accent/10 text-content'
                      : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                    <span className="block text-sm font-semibold">{t}</span>
                    <span className="block text-xs text-content-3">{sub}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Nombre *</label>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Ej. Salsa Intermedio" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {tipo === 'clase' && <div>
                <label className={labelCls}>Estilo</label>
                <select className={inputCls} value={styleId} onChange={(e) => setStyleId(e.target.value)}>
                  <option value="">— Sin estilo —</option>
                  {cat?.styles.map((s) => <option key={s.style_id} value={s.style_id}>{s.name}</option>)}
                </select>
              </div>}
              <div>
                <label className={labelCls}>Salón</label>
                <select className={inputCls} value={classroomId} onChange={(e) => setClassroomId(e.target.value)}>
                  <option value="">— Sin salón —</option>
                  {cat?.classrooms.map((r) => <option key={r.classroom_id} value={r.classroom_id}>{r.name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>{tipo === 'renta' ? 'Responsable (opcional)' : 'Profesor líder'}</label>
                <select className={inputCls} value={leaderId} onChange={(e) => setLeaderId(e.target.value)}>
                  <option value="">— Sin profesor —</option>
                  {cat?.teachers.map((t) => <option key={t.account_id} value={t.account_id}>{t.name}</option>)}
                </select>
              </div>
              {tipo === 'clase' && <div>
                <label className={labelCls}>Profesor follower (opcional)</label>
                <select className={inputCls} value={followerId} onChange={(e) => setFollowerId(e.target.value)}>
                  <option value="">— Ninguno —</option>
                  {cat?.teachers.map((t) => <option key={t.account_id} value={t.account_id}>{t.name}</option>)}
                </select>
              </div>}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {tipo === 'clase' && <>
              <div>
                <label className={labelCls}>Cupo</label>
                <input className={inputCls} value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="Ej. 20" />
              </div>
              <div>
                <label className={labelCls}>Nivel</label>
                <input className={inputCls} value={levelCode} onChange={(e) => setLevelCode(e.target.value)} placeholder="Ej. Básico" />
              </div>
              <div>
                <label className={labelCls}>Precio de clase suelta</label>
                <input className={inputCls} value={precio} onChange={(e) => setPrecio(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal" placeholder="General" data-testid="precio-clase-suelta" />
                <p className="mt-0.5 text-[11px] text-content-3">Vacío = el precio general (entre semana / sábado).</p>
              </div>
              </>}
              <div>
                <label className={labelCls}>Color</label>
                <input type="color" className="h-[38px] w-full rounded-lg border border-line bg-surface px-1" value={color} onChange={(e) => setColor(e.target.value)} />
              </div>
            </div>

            <div>
              <label className={labelCls}>Descripción</label>
              <textarea className={`${inputCls} min-h-[60px] resize-y`} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" />
            </div>

            {tipo === 'renta' ? (
              <div>
                <label className={labelCls}>Fecha *</label>
                <input type="date" className={inputCls} value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="fecha-renta" />
                <p className="mt-1 text-xs text-content-3">
                  Solo aparece ese día en el calendario{fecha ? ` (${DAYS[weekdayDe(fecha)].toLowerCase()})` : ''}; no se repite en otras semanas.
                </p>
              </div>
            ) : (
            /* Vigencia del ciclo: fuera de este rango la clase no sale en el calendario */
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Inicia</label>
                <input type="date" className={inputCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Termina</label>
                <input type="date" className={inputCls} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <p className="text-xs text-content-3 sm:col-span-2">
                El horario de abajo se repite cada semana <strong>solo entre estas dos fechas</strong>. Si las
                dejas vacías, la clase se considera siempre vigente.
              </p>
            </div>
            )}

            {/* Editor de horario semanal — SIMPLE por defecto (una hora + días marcados) */}
            {!avanzado || tipo === 'renta' ? (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className={labelCls + ' mb-0'}>{tipo === 'renta' ? 'Horario' : 'Horario semanal'}</label>
                  {tipo === 'clase' && <button onClick={() => setAvanzado(true)}
                    className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                    Horas distintas por día
                  </button>}
                </div>
                <div className="space-y-3 rounded-lg border border-line bg-surface-2 p-3">
                  {tipo === 'clase' && <div>
                    <label className="mb-1.5 block text-[10px] uppercase text-content-3">Días</label>
                    <div className="flex flex-wrap gap-1.5">
                      {DAY_ORDER.map((d) => {
                        const on = dias.includes(d);
                        return (
                          <button key={d} type="button" onClick={() => toggleDia(d)}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold uppercase transition ${on
                              ? 'bg-accent text-on-accent'
                              : 'border border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                            {DAYS[d].slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>}
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="w-28">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Inicio</label>
                      <input type="time" className={inputCls} value={blocks[0]?.start_time || ultimaBase.current?.start_time || '18:00'}
                        onChange={(e) => setHoraTodos({ start_time: e.target.value })} />
                    </div>
                    <div className="w-28">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Fin</label>
                      <input type="time" className={inputCls} value={blocks[0]?.end_time || ultimaBase.current?.end_time || '19:00'}
                        onChange={(e) => setHoraTodos({ end_time: e.target.value })} />
                    </div>
                    <div className="min-w-[8rem] flex-1">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Salón</label>
                      <select className={inputCls} value={blocks[0]?.classroom_id ?? ultimaBase.current?.classroom_id ?? ''}
                        onChange={(e) => setHoraTodos({ classroom_id: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">— Igual que la clase —</option>
                        {cat?.classrooms.map((r) => <option key={r.classroom_id} value={r.classroom_id}>{r.name}</option>)}
                      </select>
                    </div>
                  </div>
                  {tipo === 'clase' && <p className="text-xs text-content-3">
                    {dias.length === 0
                      ? 'Marca los días en que se da la clase.'
                      : `Se dará ${dias.length === 1 ? 'los' : 'los'} ${DAY_ORDER.filter((d) => dias.includes(d)).map((d) => DAYS[d].toLowerCase()).join(', ')} de ${blocks[0]?.start_time} a ${blocks[0]?.end_time}.`}
                  </p>}
                </div>
              </div>
            ) : (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className={labelCls + ' mb-0'}>Horario semanal</label>
                <div className="flex items-center gap-2">
                  {mismoHorario && (
                    <button onClick={() => setAvanzado(false)}
                      className="rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                      Volver a lo simple
                    </button>
                  )}
                  <button onClick={addBlock} className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                    <Plus size={14} /> Agregar bloque
                  </button>
                </div>
              </div>
              {blocks.length === 0 && <p className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-content-3">Sin bloques. La clase no aparecerá en el calendario hasta que agregues al menos uno.</p>}
              <div className="space-y-2">
                {blocks.map((bl, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-surface-2 p-2.5">
                    <div className="min-w-[7rem] flex-1">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Día</label>
                      <select className={inputCls} value={bl.weekday} onChange={(e) => updateBlock(i, { weekday: Number(e.target.value) })}>
                        {DAY_ORDER.map((d) => <option key={d} value={d}>{DAYS[d]}</option>)}
                      </select>
                    </div>
                    <div className="w-28">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Inicio</label>
                      <input type="time" className={inputCls} value={bl.start_time} onChange={(e) => updateBlock(i, { start_time: e.target.value })} />
                    </div>
                    <div className="w-28">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Fin</label>
                      <input type="time" className={inputCls} value={bl.end_time} onChange={(e) => updateBlock(i, { end_time: e.target.value })} />
                    </div>
                    <div className="min-w-[7rem] flex-1">
                      <label className="mb-1 block text-[10px] uppercase text-content-3">Salón</label>
                      <select className={inputCls} value={bl.classroom_id ?? ''} onChange={(e) => updateBlock(i, { classroom_id: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">— Igual que la clase —</option>
                        {cat?.classrooms.map((r) => <option key={r.classroom_id} value={r.classroom_id}>{r.name}</option>)}
                      </select>
                    </div>
                    <button onClick={() => removeBlock(i)} className="mb-1 rounded-lg p-2 text-content-3 transition hover:bg-red-500/10 hover:text-red-500" aria-label="Quitar bloque"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        )}

        <div className="sticky bottom-0 border-t border-line bg-surface">
        {/* El aviso va DENTRO de la barra fija: abajo del formulario largo no se veía al dar «Crear clase». */}
        {avisoSinFin && (
          <div className="bg-amber-500/10 px-5 py-3 text-sm text-content" data-testid="aviso-sin-fin">
            <p className="font-semibold">Esta clase no tiene fecha de fin: se va a repetir TODAS las semanas.</p>
            <p className="mt-0.5 text-xs text-content-2">Si es una renta o un ensayo, regístralo como de un solo día.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={() => cambiarTipo('renta')}
                className="rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent">Es renta o ensayo de un día</button>
              <button onClick={() => { setAvisoSinFin(false); submit(true); }}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-content-2">Sí, es clase semanal</button>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button onClick={() => submit()} disabled={saving || loading || !name.trim()}
            className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-40">
            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : tipo === 'renta' ? 'Registrar renta' : 'Crear clase'}
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
